// useToday — returns the current local YYYY-MM-DD and re-renders when the
// calendar day rolls over. Designed for date-keyed query URLs (e.g. the
// dose list, daily log) on screens that can stay alive across midnight,
// notably an installed PWA where the React tree survives background/
// foreground cycles. A static `todayYmd()` captured at mount sticks to
// yesterday's date until something else forces a re-render; this hook
// removes that footgun by making the date reactive.
//
// Rollover triggers:
//   - setTimeout aimed at the next local 00:00:00 + a 500 ms buffer,
//     covering the case where the app is in the foreground across midnight.
//   - visibilitychange → visible. Android may coalesce or delay the
//     setTimeout during OS suspend, so we re-check explicitly when the
//     PWA comes forward. This is the path that fixes the reported bug.
//   - focus. iOS PWAs don't always fire visibilitychange reliably; focus
//     gives us a redundant signal at near-zero cost.
//
// setState only fires when the YMD string actually changes, so callers
// that mount before midnight don't re-render needlessly during the day.
//
// `msUntilNextMidnight` is exported pure for unit tests.

import { useEffect, useState } from 'react';
import { todayYmd } from './format';

export function msUntilNextMidnight(now: Date): number {
  const next = new Date(now);
  next.setHours(24, 0, 0, 500);
  return Math.max(1000, next.getTime() - now.getTime());
}

export function useToday(): string {
  const [today, setToday] = useState<string>(todayYmd);

  useEffect(() => {
    let timeoutId: number | undefined;

    const checkAndReschedule = (): void => {
      setToday((prev) => {
        const next = todayYmd();
        return prev === next ? prev : next;
      });
      if (timeoutId != null) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(checkAndReschedule, msUntilNextMidnight(new Date()));
    };

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') checkAndReschedule();
    };

    timeoutId = window.setTimeout(checkAndReschedule, msUntilNextMidnight(new Date()));
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      if (timeoutId != null) window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, []);

  return today;
}
